import { useMemo, useState } from 'react'
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Check, Search } from "lucide-react"
import { cn } from "@/lib/utils"

export interface SchedulingOption {
    /** Unique value for selection (event type URI, meeting link URL, ...) */
    id: string;
    title: string;
    /** Short qualifier rendered as a badge (e.g. "30 min", "round robin") */
    badge?: string;
    /** Secondary line (description or URL), truncated to two lines */
    subtitle?: string;
}

interface SchedulingOptionListProps {
    options: SchedulingOption[];
    selectedId?: string | null;
    onSelect: (id: string) => void;
    /** Search shows automatically once the list is long enough to need it */
    searchPlaceholder?: string;
}

const SEARCH_THRESHOLD = 6;

export default function SchedulingOptionList({
    options,
    selectedId,
    onSelect,
    searchPlaceholder = 'Search...',
}: SchedulingOptionListProps) {
    const [query, setQuery] = useState('');

    const filteredOptions = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) return options;
        return options.filter(option =>
            `${option.title} ${option.subtitle || ''} ${option.badge || ''}`.toLowerCase().includes(trimmed)
        );
    }, [options, query]);

    return (
        <div className="grid gap-2">
            {options.length >= SEARCH_THRESHOLD && (
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="pl-8"
                    />
                </div>
            )}
            <div
                className="max-h-72 overflow-y-auto rounded-lg border p-1.5 grid gap-1.5"
                role="radiogroup"
            >
                {filteredOptions.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                        No matches
                    </div>
                ) : (
                    filteredOptions.map((option) => {
                        const isSelected = option.id === selectedId;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                onClick={() => onSelect(option.id)}
                                className={cn(
                                    "w-full rounded-md border p-3 text-left transition-colors",
                                    isSelected
                                        ? "border-primary bg-primary/5"
                                        : "border-transparent hover:bg-muted"
                                )}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate text-sm font-medium">
                                                {option.title}
                                            </span>
                                            {option.badge && (
                                                <Badge variant="secondary" className="flex-shrink-0 font-normal">
                                                    {option.badge}
                                                </Badge>
                                            )}
                                        </div>
                                        {option.subtitle && (
                                            <p className="mt-0.5 line-clamp-2 break-all text-xs text-muted-foreground">
                                                {option.subtitle}
                                            </p>
                                        )}
                                    </div>
                                    <span
                                        className={cn(
                                            "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border",
                                            isSelected
                                                ? "border-primary bg-primary text-primary-foreground"
                                                : "border-muted-foreground/40"
                                        )}
                                    >
                                        {isSelected && <Check className="h-3 w-3" />}
                                    </span>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
